/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { defineMessages } from 'react-intl';

const messages = defineMessages({
  environment: {
    id: 'components.app.analytics.environment',
    defaultMessage: 'Environment'
  },
  draft: {
    id: 'components.app.analytics.draft',
    defaultMessage: 'Draft'
  },
  live: {
    id: 'components.app.analytics.live',
    defaultMessage: 'Live'
  },
  improveEngagement: {
    id: 'components.app.analytics.improveEngagement',
    defaultMessage: 'Improve engagement'
  },
  improveEngagementDescription: {
    id: 'components.app.analytics.improveEngagementDescription',
    defaultMessage:
      'Use the suggestions below as a starting point for your analysis to find ways to improve the performance of your assistant.'
  },
  improveContainment: {
    id: 'components.app.analytics.improveContainment',
    defaultMessage: 'Improve containment'
  },
  improveContainmentDescription: {
    id: 'components.app.analytics.improveContainmentDescription',
    defaultMessage:
      'Use the suggestions below as a starting point for your analysis to find ways to reduce the number of conversations that need human intervention.'
  },
  improveCoverage: {
    id: 'components.app.analytics.improveCoverage',
    defaultMessage: 'Improve coverage'
  },
  improveCoverageDescription: {
    id: 'components.app.analytics.improveCoverageDescription',
    defaultMessage:
      'Use the suggestions below as a starting point for your analysis to find ways to increase the number of messages your assistant can respond to.'
  },
  engagementTab: {
    id: 'components.app.analytics.engagementTab',
    defaultMessage: 'Engagement trend'
  },
  recognitionTab: {
    id: 'components.app.analytics.recognitionTab',
    defaultMessage: 'Recognition trend'
  },
  containmentTab: {
    id: 'components.app.analytics.containmentTab',
    defaultMessage: 'Containment trend'
  },
  showActiveUsers: {
    id: 'components.app.analytics.showActiveUsers',
    defaultMessage: 'Show active users'
  },
  showConversations: {
    id: 'components.app.analytics.showConversations',
    defaultMessage: 'Show conversations'
  },
  showRequests: {
    id: 'components.app.analytics.showRequests',
    defaultMessage: 'Show requests'
  },
  recognition: {
    id: 'components.app.analytics.recognition',
    defaultMessage: 'Recognition'
  },
  averageRecognition: {
    id: 'components.app.analytics.averageRecognition',
    defaultMessage: 'Average recognition'
  },
  convoSearch: {
    id: 'components.app.analytics.convoSearch',
    defaultMessage: 'Conversational search'
  },
  viewConvoSearch: {
    id: 'components.app.analytics.viewConvoSearch',
    defaultMessage: 'View conversational search responses'
  },
  averagePerformance: {
    id: 'components.app.analytics.averagePerformance',
    defaultMessage: 'Average performance'
  },
  responseConfidence: {
    id: 'components.app.analytics.responseConfidence',
    defaultMessage: 'Response confidence'
  },
  retrievalConfidence: {
    id: 'components.app.analytics.retrievalConfidence',
    defaultMessage: 'Retrieval confidence'
  },
  extractiveness: {
    id: 'components.app.analytics.extractiveness',
    defaultMessage: 'Extractiveness'
  },
  citations: {
    id: 'components.app.analytics.citations',
    defaultMessage: 'Citations'
  },
  perResponse: {
    id: 'components.app.analytics.perResponse',
    defaultMessage: 'per response'
  },
  responseLength: {
    id: 'components.app.analytics.responseLength',
    defaultMessage: 'Response length'
  },
  response: {
    id: 'components.app.analytics.response',
    defaultMessage: 'Response'
  },
  characters: {
    id: 'components.app.analytics.characters',
    defaultMessage: 'Characters'
  },
  recognized: {
    id: 'components.app.analytics.recognized',
    defaultMessage: 'All recognized'
  },
  notRecognized: {
    id: 'components.app.analytics.notRecognized',
    defaultMessage: 'Not recognized'
  },
  unrecognized: {
    id: 'modules.app.analytics.unrecognized',
    defaultMessage: 'Unrecognized'
  },
  noMatches: {
    id: 'modules.app.analytics.noMatches',
    defaultMessage: 'No matches'
  },
  customBuiltActions: {
    id: 'components.app.analytics.customBuiltActions',
    defaultMessage: 'Custom-built actions'
  },
  skillBasedActions: {
    id: 'components.app.analytics.skillBasedActions',
    defaultMessage: 'Skill-based actions'
  },
  aiGuidedActions: {
    id: 'components.app.analytics.aiGuidedActions',
    defaultMessage: 'AI-guided actions'
  },
  generalPurposeAnswering: {
    id: 'components.app.analytics.generalPurposeAnswering',
    defaultMessage: 'General purpose answering'
  },
  conversationalSearch: {
    id: 'components.app.analytics.conversationalSearch',
    defaultMessage: 'Conversational search'
  },
  search: {
    id: 'components.app.analytics.search',
    defaultMessage: 'Search'
  },
  search0results: {
    id: 'components.app.analytics.search0results',
    defaultMessage: 'Search with 0 results'
  },
  systemActions: {
    id: 'components.app.analytics.systemActions',
    defaultMessage: 'System actions'
  },
  intents: {
    id: 'components.app.analytics.intents',
    defaultMessage: 'Intents'
  },
  recognizedRequests: {
    id: 'components.app.analytics.recognizedRequests',
    defaultMessage: 'Recognized requests'
  },
  unrecognizedRequests: {
    id: 'components.app.analytics.unrecognizedRequests',
    defaultMessage: 'Unrecognized requests'
  },
  viewUnrecognizedRequests: {
    id: 'components.app.analytics.viewUnrecognizedRequests',
    defaultMessage: 'View unrecognized requests'
  },
  skillBased: {
    id: 'components.app.analytics.skillBased',
    defaultMessage: 'Skill-based action'
  },
  aiGuided: {
    id: 'components.app.analytics.aiGuided',
    defaultMessage: 'AI-guided action'
  },
  customBuilt: {
    id: 'components.app.analytics.customBuilt',
    defaultMessage: 'Custom-built action'
  },
  generalPurpose: {
    id: 'components.app.analytics.generalPurpose',
    defaultMessage: 'General purpose answering'
  },

  notRecognizedRequests: {
    id: 'components.app.analytics.notRecognizedRequests',
    defaultMessage: 'Requests not recognized'
  },
  containment: {
    id: 'components.app.analytics.containment',
    defaultMessage: 'Containment'
  },
  contained: {
    id: 'components.app.analytics.contained',
    defaultMessage: 'Contained'
  },
  escalated: {
    id: 'components.app.analytics.escalated',
    defaultMessage: 'Escalated'
  },
  containedConversations: {
    id: 'components.app.analytics.containedConversations',
    defaultMessage: 'Contained conversations'
  },
  escalatedConversations: {
    id: 'components.app.analytics.escalatedConversations',
    defaultMessage: 'Escalated conversations'
  },
  customRange: {
    id: 'components.app.analytics.customRange',
    defaultMessage: 'Custom range'
  },
  today: {
    id: 'components.app.analytics.today',
    defaultMessage: 'Today'
  },
  thisWeek: {
    id: 'components.app.analytics.thisWeek',
    defaultMessage: 'This week'
  },
  twoWeeks: {
    id: 'components.app.analytics.twoWeeks',
    defaultMessage: 'Past 2 weeks'
  },
  threeWeeks: {
    id: 'components.app.analytics.threeWeeks',
    defaultMessage: 'Past 3 weeks'
  },
  thirtyDays: {
    id: 'components.app.analytics.thirtyDays',
    defaultMessage: 'Past 30 days'
  },
  ninetyDays: {
    id: 'components.app.analytics.ninetyDays',
    defaultMessage: 'Past 90 days'
  },
  timePeriod: {
    id: 'components.app.analytics.timePeriod',
    defaultMessage: 'Select date range'
  },
  additionalFilters: {
    id: 'components.app.analytics.additionalFilters',
    defaultMessage: 'Additional filters'
  },
  showFilters: {
    id: 'components.app.analytics.showFilters',
    defaultMessage: 'Show filters'
  },
  all: {
    id: 'components.app.analytics.all',
    defaultMessage: 'All'
  },
  escalatedOnly: {
    id: 'components.app.analytics.escalatedOnly',
    defaultMessage: 'Escalated'
  },
  containedOnly: {
    id: 'components.app.analytics.containedOnly',
    defaultMessage: 'Contained'
  },
  recognizedOnly: {
    id: 'components.app.analytics.recognizedOnly',
    defaultMessage: 'Recognized'
  },
  notRecognizedOnly: {
    id: 'components.app.analytics.notRecognizedOnly',
    defaultMessage: 'Not recognized'
  },
  search: {
    id: 'components.app.analytics.search',
    defaultMessage: 'Search'
  },
  initiatingSearch: {
    id: 'components.app.analytics.initiatingSearch',
    defaultMessage: 'Requests initiating search'
  },
  zeroResults: {
    id: 'components.app.analytics.zeroResults',
    defaultMessage: 'Search with 0 results'
  },
  rangeDropdown: {
    id: 'components.app.analytics.rangeDropdown',
    defaultMessage: 'Range dropdown selection'
  },
  datePicker: {
    id: 'components.app.analytics.datePicker',
    defaultMessage: 'Date picker'
  },
  refreshData: {
    id: 'modules.app.analytics.refreshData',
    defaultMessage: 'Refresh data'
  },
  refresh: {
    id: 'modules.app.analytics.refresh',
    defaultMessage: 'Refresh'
  },
  lastUpdated: {
    id: 'modules.app.analytics.lastUpdated',
    defaultMessage: 'Updated at'
  },
  overview: {
    id: 'modules.app.analytics.overview',
    defaultMessage: 'Overview'
  },
  analyticsOverview: {
    id: 'modules.app.analytics.analyticsOverview',
    defaultMessage: 'Analytics overview'
  },
  actions: {
    id: 'modules.app.analytics.actions',
    defaultMessage: 'Actions'
  },
  actionCompletion: {
    id: 'modules.app.analytics.actionCompletion',
    defaultMessage: 'Action completion'
  },
  viewActionCompletion: {
    id: 'modules.app.analytics.viewActionCompletion',
    defaultMessage: 'View action completion'
  },
  recognizedActions: {
    id: 'modules.app.analytics.recognizedActions',
    defaultMessage: 'Recognized actions'
  },
  unrecognizedActions: {
    id: 'modules.app.analytics.unrecognizedActions',
    defaultMessage: 'Unrecognized actions'
  },
  performance: {
    id: 'modules.app.analytics.performance',
    defaultMessage: 'Performance'
  },
  requestRecognition: {
    id: 'modules.analytics.requestRecognition',
    defaultMessage: 'Request recognition'
  },
  conversations: {
    id: 'modules.analytics.components.ConvoLogs.conversations',
    defaultMessage: 'Conversations'
  },
  topics: {
    id: 'modules.analytics.components.ConvoLogs.topics',
    defaultMessage: 'Topics'
  },
  entities: {
    id: 'modules.analytics.components.ConvoLogs.entities',
    defaultMessage: 'Entities'
  },
  requests: {
    id: 'modules.analytics.components.ConvoLogs.requests',
    defaultMessage: 'Request'
  },
  escalatedAgent: {
    id: 'modules.analytics.components.ConvoLogs.escalated',
    defaultMessage: 'Escalated to agent'
  },
  info: {
    id: 'modules.analytics.components.ConvoLogs.info',
    defaultMessage: 'Info'
  },
  warning: {
    id: 'modules.analytics.components.ConvoLogs.warning',
    defaultMessage: 'Warning'
  },
  warningTooltip: {
    id: 'modules.analytics.components.ConvoLogs.warningTooltip',
    defaultMessage: 'The request was not routed to a topic'
  },
  conditionTooltip: {
    id: 'modules.analytics.components.ConvoLogs.conditionTooltip',
    defaultMessage: `Action wasn't triggered because the conditions weren't met`
  },
  spellCheck: {
    id: 'modules.analytics.components.ConvoLogs.spellCheck',
    defaultMessage: 'Spell Check'
  },
  noConversationsFoundDescription: {
    id: 'components.app.analytics.noConversationsFoundDescription',
    defaultMessage:
      'Check to make sure your assistant is live and conversations occurred during the selected date range.'
  },
  noActionsFound: {
    id: 'components.app.analytics.noActionsFound',
    defaultMessage: 'No actions found'
  },
  noActionsFoundDescriptionCompletion: {
    id: 'components.app.analytics.noActionsFoundDescription',
    defaultMessage:
      'You need to create at least one action for your assistant before tracking completion.'
  },
  noActionsFoundDescriptionRecognition: {
    id: 'components.app.analytics.noActionsFoundDescriptionRecognition',
    defaultMessage:
      'You need to create at least one action for your assistant before tracking recognition.'
  },
  noActionsFoundDescriptionRequests: {
    id: 'components.app.analytics.noActionsFoundDescriptionRequests',
    defaultMessage:
      'You need to create at least one action before requests can be displayed.'
  },
  noActionsFoundDescriptionDialog: {
    id: 'components.app.analytics.noActionsFoundDescriptionDialog',
    defaultMessage:
      'Create at least one action and link it in dialog so the assistant can track completion.'
  },
  notPublishedDescriptionCompletion: {
    id: 'components.app.analytics.notPublishedDescriptionCompletion',
    defaultMessage:
      'You need to publish your assistant before tracking completion.'
  },
  notPublishedDescriptionRecognition: {
    id: 'components.app.analytics.notPublishedDescriptionRecognition',
    defaultMessage:
      'You need to publish your assistant before tracking recognition.'
  },
  notPublishedDescriptionConvoSearch: {
    id: 'components.app.analytics.notPublishedDescriptionConvoSearch',
    defaultMessage:
      'You need to publish your assistant before tracking conversational search.'
  },
  notPublishedDescriptionRequests: {
    id: 'components.app.analytics.notPublishedDescriptionRequests',
    defaultMessage:
      'You need to publish your assistant before requests can be displayed.'
  },
  notPublishedDescription: {
    id: 'components.app.analytics.notPublishedDescriptionRequests',
    defaultMessage: 'You need to publish your assistant.'
  },
  assistantNotPublished: {
    id: 'components.app.analytics.assistantNotPublished',
    defaultMessage: 'Assistant not published'
  },
  createNewAction: {
    id: 'components.app.analytics.createNewAction',
    defaultMessage: 'Create a new action'
  },
  hourly: {
    id: 'components.app.analytics.hourly',
    defaultMessage: 'Hourly'
  },
  daily: {
    id: 'components.app.analytics.daily',
    defaultMessage: 'Daily'
  },
  weekly: {
    id: 'components.app.analytics.weekly',
    defaultMessage: 'Weekly'
  },
  monthly: {
    id: 'components.app.analytics.monthly',
    defaultMessage: 'Monthly'
  },
  hour: {
    id: 'components.app.analytics.hour',
    defaultMessage: 'Hour'
  },
  day: {
    id: 'components.app.analytics.day',
    defaultMessage: 'Day'
  },
  week: {
    id: 'components.app.analytics.week',
    defaultMessage: 'Week'
  },
  month: {
    id: 'components.app.analytics.month',
    defaultMessage: 'Month'
  },
  completionPercent: {
    id: 'modules.app.analytics.completionPercent',
    defaultMessage: 'Completion %'
  },
  completion: {
    id: 'modules.app.analytics.completion',
    defaultMessage: 'Completion'
  },
  completionPercentage: {
    id: 'modules.app.analytics.completionPercentage',
    defaultMessage: 'Completion percentage'
  },
  averageCompletion: {
    id: 'modules.app.analytics.averageCompletion',
    defaultMessage: 'Average completion'
  },
  completeActions: {
    id: 'modules.app.analytics.completeActions',
    defaultMessage: 'Complete actions'
  },
  completed: {
    id: 'modules.app.analytics.completed',
    defaultMessage: 'Completed'
  },
  complete: {
    id: 'modules.app.analytics.complete',
    defaultMessage: 'Complete'
  },
  sessionId: {
    id: 'modules.app.analytics.session.id',
    defaultMessage: 'Session ID: {id}'
  },
  copyToClipboard: {
    id: 'modules.app.analytics.copyToClipboard',
    defaultMessage: 'Copy to clipboard'
  },
  incompleteActions: {
    id: 'modules.app.analytics.incompleteActions',
    defaultMessage: 'Incomplete actions'
  },
  incomplete: {
    id: 'modules.app.analytics.incomplete',
    defaultMessage: 'Incomplete'
  },
  singleTopicIncompleteTooltip: {
    id: 'modules.app.analytics.singleTopicIncompleteTooltip',
    defaultMessage:
      'This chart presents the various reasons this action was marked as incomplete and how often those reasons occurred.'
  },
  singleTopicCompleteTooltip: {
    id: 'modules.app.analytics.singleTopicCompleteTooltip',
    defaultMessage:
      'This chart presents the various reasons this action was marked as complete and how often those reasons occurred.'
  },
  learnMore: {
    id: 'modules.app.analytics.learnMore',
    defaultMessage: 'Learn more'
  },
  other: {
    id: 'modules.app.analytics.other',
    defaultMessage: 'Other'
  },
  totalParentheses: {
    id: 'modules.app.analytics.totalParentheses',
    defaultMessage: ' (total):'
  },
  abandonedOrOngoing: {
    id: 'modules.app.analytics.abandonedOrOngoing',
    defaultMessage: 'Abandoned or ongoing: '
  },
  abandoned: {
    id: 'modules.app.analytics.abandoned',
    defaultMessage: 'Abandoned: '
  },
  escalatedToAgent: {
    id: 'modules.app.analytics.escalatedToAgent',
    defaultMessage: 'Escalated to agent: '
  },
  startedNewAction: {
    id: 'modules.app.analytics.startedNewAction',
    defaultMessage: 'Started new action: '
  },
  stuckOnStep: {
    id: 'modules.app.analytics.stuckOnStep',
    defaultMessage: 'Stuck on step: '
  },
  triggerPhrase: {
    id: 'modules.app.analytics.triggerPhrase',
    defaultMessage: 'Trigger phrase: '
  },
  completedByAssistant: {
    id: 'modules.app.analytics.completedByAssistant',
    defaultMessage: 'Completed by assistant: '
  },
  completedByAgent: {
    id: 'modules.app.analytics.completedByAgent',
    defaultMessage: 'Completed by agent handoff: '
  },
  percentageCompletion: {
    id: 'modules.app.analytics.percentageCompletion',
    defaultMessage: 'Completion: '
  },
  incompleteChartHeader: {
    id: 'modules.app.analytics.incompleteChartHeader',
    defaultMessage: 'Why was {name} incomplete?'
  },
  completeChartHeader: {
    id: 'modules.app.analytics.completeChartHeader',
    defaultMessage: 'How was {name} completed?'
  },
  percentageChartHeader: {
    id: 'modules.app.analytics.completeChartHeader',
    defaultMessage: 'How often is {name} completed?'
  },
  transferredAction: {
    id: 'modules.app.analytics.transferredAction',
    defaultMessage: 'Transferred to {name} action'
  },
  howActionStarted: {
    id: 'modules.app.analytics.howActionStarted',
    defaultMessage: 'How was the action started?'
  },
  assistant: {
    id: 'modules.app.analytics.assistant',
    defaultMessage: 'Assistant'
  },
  agentHandoff: {
    id: 'modules.app.analytics.agentHandoff',
    defaultMessage: 'Agent handoff'
  },
  searchCalled: {
    id: 'modules.app.analytics.searchCalled',
    defaultMessage: 'Search called'
  },
  recognizedActionsCompleted: {
    id: 'modules.app.analytics.recognizedActionsCompleted',
    defaultMessage: 'How often are your recognized actions completed?'
  },
  actionsCompletion: {
    id: 'modules.app.analytics.actionsCompletion',
    defaultMessage: 'How often are your assistant’s actions completed?'
  },
  actionsRecognition: {
    id: 'modules.app.analytics.actionsRecognition',
    defaultMessage: 'How often are requests recognized by your assistant?'
  },
  completedActionsTooltip: {
    id: 'modules.app.analytics.completedActionsTooltip',
    defaultMessage:
      'Completion measures how often within a given time period users reach the end step of an action.'
  },
  recognitionActionsTooltip: {
    id: 'modules.app.analytics.recognitionActionsTooltip',
    defaultMessage:
      'Recognition measures the requests within a given time period that are recognized and successfully routed to an action.'
  },
  recognitionTopicsTooltip: {
    id: 'modules.app.analytics.recognitionActionsTooltip',
    defaultMessage:
      'Recognition measures the requests within a given time period that are recognized and successfully routed to a topic.'
  },
  spellCheckTooltip: {
    id: 'modules.analytics.components.ConvoLogs.spellCheckTooltip',
    defaultMessage: 'Autocorrection was used to route to a topic'
  },
  searchColon: {
    id: 'modules.analytics.components.ConvoLogs.search',
    defaultMessage: 'Search: '
  },
  results: {
    id: 'modules.analytics.components.ConvoLogs.results',
    defaultMessage: ' results'
  },
  unableToConnect: {
    id: 'components.app.analytics.unableToConnect',
    defaultMessage:
      'Unable to load the conversation. Please close the panel and reopen it.'
  },
  close: {
    id: 'components.app.analytics.close',
    defaultMessage: 'Close'
  },
  publishAssistant: {
    id: 'modules.app.analytics.publishAssistant',
    defaultMessage: 'Publish your assistant'
  },
  requestsPerPage: {
    id: 'modules.analytics.components.ConvoLogs.requestsPerPage',
    defaultMessage: 'Requests per page'
  },
  itemsPerPage: {
    id: 'modules.analytics.components.ConvoLogs.itemsPerPage',
    defaultMessage: 'Items per page'
  },
  searchKeyword: {
    id: 'components.app.analytics.searchKeyword',
    defaultMessage: 'Enter keyword or session ID to filter by'
  },
  keyword: {
    id: 'components.app.analytics.keyword',
    defaultMessage: 'Search'
  },
  findTopics: {
    id: 'components.app.analytics.findTopics',
    defaultMessage: 'Find topics...'
  },
  filterEnvironmentTitle: {
    id: 'components.app.analytics.filterEnvironmentTitle',
    defaultMessage: 'Environment'
  },
  filterEnvironmentPlaceholder: {
    id: 'components.app.analytics.filterEnvironmentPlaceholder',
    defaultMessage: 'Filter by environment'
  },
  filterRecognitionTypeTitle: {
    id: 'components.app.analytics.filterRecognitionTypeTitle',
    defaultMessage: 'Recognition type'
  },
  filterRecognitionTypePlaceholder: {
    id: 'components.app.analytics.filterRecognitionTypePlaceholder',
    defaultMessage: 'Filter by action or recognition type'
  },
  recognitionTypeColumnTooltipText: {
    id: 'components.app.analytics.recognitionTypeColumnTooltipText',
    defaultMessage:
      'In this column, you can see what the assistant recognized each conversation as i.e. action, intent, answering etc.'
  },
  recognitionTypeFilterTooltipText: {
    id: 'components.app.analytics.recognitionTypeFilterTooltipText',
    defaultMessage:
      'Filter by what the assistant recognized a user’s input should route through i.e. action, intent, answering etc.'
  },
  filterActionTitle: {
    id: 'components.app.analytics.filterActionTitle',
    defaultMessage: 'Action'
  },
  filterActionPlaceholder: {
    id: 'components.app.analytics.filterActionPlaceholder',
    defaultMessage: 'Filter by action'
  },
  filterEntityTitle: {
    id: 'components.app.analytics.filterEntityTitle',
    defaultMessage: 'Entity'
  },
  filterEntityPlaceholder: {
    id: 'components.app.analytics.filterEntityPlaceholder',
    defaultMessage: 'Filter by entity'
  },
  filterActions: {
    id: 'components.app.analytics.filterActions',
    defaultMessage: 'Filter by actions'
  },
  filterTopics: {
    id: 'components.app.analytics.filterTopics',
    defaultMessage: 'Filter by topics'
  },
  filterEntities: {
    id: 'components.app.analytics.filterEntities',
    defaultMessage: 'Fitler by entities'
  },
  filterEntitiesTooltip: {
    id: 'components.app.analytics.filterEntitiesTooltip',
    defaultMessage: 'This filter only applies to the Message log view'
  },
  irrelevant: {
    id: 'components.app.analytics.irrelevant',
    defaultMessage: 'Irrelevant'
  },
  clearFilters: {
    id: 'components.app.analytics.clearFilters',
    defaultMessage: 'Clear filters'
  },
  noRequests: {
    id: 'components.app.analytics.noRequests',
    defaultMessage: 'No requests'
  },
  noConversationsDescription: {
    id: 'components.app.analytics.noConversationsDescription',
    defaultMessage:
      'Check to make sure your assistant is in production and conversations occurred during the selected date range.'
  },
  noRequestsDescription: {
    id: 'components.app.analytics.noRequestsDescription',
    defaultMessage:
      'There are no requests for the selected date range and/or filters.'
  },
  searchTerm: {
    id: 'modules.app.analytics.searchTerm',
    defaultMessage: 'Search: {value}'
  },
  untitledAction: {
    id: 'components.app.analytics.untitledAction',
    defaultMessage: 'Untitled action'
  },
  integrations: {
    id: 'components.app.analytics.integrations',
    defaultMessage: 'Integrations'
  },
  actionsPerPage: {
    id: 'modules.analytics.components.ConvoLogs.actionsPerPage',
    defaultMessage: 'Actions per page:'
  },
  noActionsCreated: {
    id: 'components.app.analytics.noActionsCreated',
    defaultMessage: 'No actions created'
  },
  noActionsDescription: {
    id: 'components.app.analytics.noActionsDescription',
    defaultMessage:
      'You need to create an action and push your assistant to production before tracking completion.'
  },
  editAction: {
    id: 'components.app.analytics.editAction',
    defaultMessage: 'Edit action'
  },
  edit: {
    id: 'components.app.analytics.edit',
    defaultMessage: 'Edit'
  },
  reason: {
    id: 'components.app.analytics.reason',
    defaultMessage: 'Reason'
  },
  requestTooltip: {
    id: 'components.app.analytics.requestTooltip',
    defaultMessage:
      'A recognized request is a user message that has started an action.'
  },
  reasons: {
    id: 'components.app.analytics.reasons',
    defaultMessage: 'Reasons'
  },
  filterByReason: {
    id: 'components.app.analytics.filterByReason',
    defaultMessage: 'Filter by reason'
  },
  totalIncomplete: {
    id: 'components.app.analytics.totalIncomplete',
    defaultMessage: 'Total incomplete'
  },
  noRequestsRecognized: {
    id: 'components.app.analytics.noRequestsRecognized',
    defaultMessage: 'No requests recognized'
  },
  noRequestsRecognizedDescription: {
    id: 'components.app.analytics.noRequestsRecognizedDescription',
    defaultMessage:
      'Check back at a later time or update this action with additional request phrases.'
  },
  timesStarted: {
    id: 'components.app.analytics.timesStarted',
    defaultMessage: 'Times started'
  },
  duration: {
    id: 'components.app.analytics.duration',
    defaultMessage: 'Duration'
  },
  searches: {
    id: 'components.app.analytics.searches',
    defaultMessage: 'Searches'
  },
  noActionsStarted: {
    id: 'components.app.analytics.noActionsStarted',
    defaultMessage: 'No actions started'
  },
  noActionsStartedDescription: {
    id: 'components.app.analytics.noActionsStartedDescription',
    defaultMessage: 'There are no started actions for the selected date range.'
  },
  title: {
    id: 'components.app.analytics.title',
    defaultMessage: 'Analyze'
  },
  mostFrequentActions: {
    id: 'components.app.analytics.mostFrequentActions',
    defaultMessage: 'Most frequent actions'
  },
  mostFrequentTopics: {
    id: 'components.app.analytics.mostFrequentTopics',
    defaultMessage: 'Most frequent topics'
  },
  leastFrequentActions: {
    id: 'components.app.analytics.leastFrequentActions',
    defaultMessage: 'Least frequent actions'
  },
  leastFrequentTopics: {
    id: 'components.app.analytics.leastFrequentTopics',
    defaultMessage: 'Least frequent topics'
  },
  leastCompletedActions: {
    id: 'components.app.analytics.leastCompletedActions',
    defaultMessage: 'Least completed actions'
  },
  overviewNoAction: {
    id: 'components.app.analytics.overviewNoAction',
    defaultMessage: 'You need to create at least one action for your assistant.'
  },
  overviewNoResult: {
    id: 'components.app.analytics.overviewNoResult',
    defaultMessage: 'Your assistant has not received any requests.'
  },
  overviewNoActionRequests: {
    id: 'components.app.analytics.overviewNoActionRequests',
    defaultMessage: 'Your assistant has not received any action requests.'
  },
  overviewNoFrequentActions: {
    id: 'components.app.analytics.overviewNoFrequentActions',
    defaultMessage: `There aren't enough actions created to show least frequent actions.`
  },
  overviewNoFrequentTopics: {
    id: 'components.app.analytics.overviewNoFrequentTopics',
    defaultMessage: `There aren't enough topics created to show least frequent topics.`
  },
  viewByTopic: {
    id: 'components.app.analytics.viewByTopic',
    defaultMessage: 'View by topic'
  },
  viewByMessages: {
    id: 'components.app.analytics.viewByMessages',
    defaultMessage: 'View by messages'
  },
  uniqueUsers: {
    id: 'components.app.analytics.uniqueUsers',
    defaultMessage: 'Unique users'
  },
  requestsTooltip: {
    id: 'components.app.analytics.requestsTooltip',
    defaultMessage:
      'A request is a user-generated message that can be recognized and start an action. A request can also be unrecognized and in need of further training.'
  },
  requestsTopicsTooltip: {
    id: 'components.app.analytics.requestsTooltip',
    defaultMessage:
      'A request is a user-generated message that can be recognized and start a topic. A request can also be unrecognized and in need of further training.'
  },
  triggerConditionRequestsTooltip: {
    id: 'components.app.analytics.triggerConditionRequestsTooltip',
    defaultMessage: `A request is a user-generated message that can be recognized and start {type}. If request does not start {type} due to a condition not met, it's still considered recognized. A request can also be unrecognized and in need of further training.`
  },
  unauthorized: {
    id: 'components.app.analytics.unauthorized',
    defaultMessage: 'Unauthorized'
  },
  error403: {
    id: 'components.app.analytics.error403',
    defaultMessage: 'Error 403'
  },
  forbidden: {
    id: 'components.app.analytics.forbidden',
    defaultMessage: "You don't have access to Analytics"
  },
  notAuthorized: {
    id: 'components.app.analytics.notAuthorized',
    defaultMessage:
      'Contact your administrator to make changes to your permissions'
  },
  singleAction: {
    id: 'components.app.analytics.singleAction',
    defaultMessage: 'Single action'
  },
  topicChanged: {
    id: 'components.app.analytics.topicChanged',
    defaultMessage: 'topic changed'
  },
  subAction: {
    id: 'components.app.analytics.subAction',
    defaultMessage: 'subaction started'
  },
  started: {
    id: 'components.app.analytics.started',
    defaultMessage: 'started'
  },
  percentageChart: {
    id: 'components.app.analytics.percentageChart',
    defaultMessage: 'Percentage chart'
  },
  volumeChart: {
    id: 'components.app.analytics.volumeChart',
    defaultMessage: 'Volume chart'
  },
  noData: {
    id: 'components.app.analytics.noData',
    defaultMessage: 'No data'
  },
  deletedParentheses: {
    id: 'components.app.analytics.deleted',
    defaultMessage: '(Deleted)'
  },
  dialogCalledAction: {
    id: 'components.app.analytics.dialogCalledAction',
    defaultMessage: '(Dialog called action)'
  },
  dataFrom30Days: {
    id: 'components.app.analytics.dataFrom30Days',
    defaultMessage: 'Data is from the past 30 days.'
  },
  groupingInProgress: {
    id: 'components.app.analytics.groupingInProgress',
    defaultMessage: 'Grouping in progress'
  },
  groupingInProgressSubtitle: {
    id: 'components.app.analytics.groupingInProgressSubtitle',
    defaultMessage:
      'This process can take up to 10 minutes. You can continue working while it runs.'
  },
  customerInput: {
    id: 'components.app.analytics.customerInput',
    defaultMessage: 'Customer input'
  },
  details: {
    id: 'components.app.analytics.details',
    defaultMessage: 'Details'
  },
  originalResponse: {
    id: 'components.app.analytics.originalResponse',
    defaultMessage: 'Original response below threshold and was not shown'
  },
  noOriginalResponse: {
    id: 'components.app.analytics.noOriginalResponse',
    defaultMessage:
      'Retrieval confidence score below threshold, no response generated'
  },
  inferredQuery: {
    id: 'components.app.analytics.inferredQuery',
    defaultMessage: 'Inferred query text:'
  },
  errorCallingSearch: {
    id: 'components.app.analytics.errorCallingSearch',
    defaultMessage: 'There are {count} or more errors calling Search'
  },
  openPanelForDetails: {
    id: 'components.app.analytics.openPanelForDetails',
    defaultMessage: 'Open panel for full details.'
  }
});

export const RecognitionMessages = defineMessages({
  close: {
    id: 'components.app.analytics.Recognition.close',
    defaultMessage: 'Close'
  },
  createNewAction: {
    id: 'components.app.analytics.Recognition.createNewAction',
    defaultMessage: 'Create new action'
  },
  createNewActionBttn: {
    id: 'components.app.analytics.Recognition.createNewActionBttn',
    defaultMessage: 'Create a new action'
  },
  download: {
    id: 'components.app.analytics.Recognition.download',
    defaultMessage: 'Download group'
  },
  downloadGroups: {
    id: 'components.app.analytics.Recognition.downloadGroups',
    defaultMessage: 'Download groups'
  },
  individually: {
    id: 'components.app.analytics.Recognition.individually',
    defaultMessage: 'Individually'
  },
  groupBySimilarity: {
    id: 'components.app.analytics.Recognition.groupBySimilarity',
    defaultMessage: 'Grouped by similarity'
  },
  lastUpdated: {
    id: 'modules.app.analytics.Recognition.lastUpdated',
    defaultMessage: 'Updated at'
  },
  relatedActions: {
    id: 'modules.app.analytics.Recognition.relatedActions',
    defaultMessage: 'Related actions'
  },
  requestCount: {
    id: 'modules.app.analytics.Recognition.requestCount',
    defaultMessage: 'Request count ({count})'
  },
  selectExamples: {
    id: 'components.app.analytics.Recognition.selectExamples',
    defaultMessage:
      'Select requests to start building a new action. Requests are used as example phrases.'
  },
  unrecognizedRequests: {
    id: 'components.app.analytics.Recognition.unrecognizedRequests',
    defaultMessage: 'Unrecognized requests'
  },
  viewRequests: {
    id: 'components.app.analytics.Recognition.viewRequests',
    defaultMessage: 'View requests'
  }
});

export const CreateActionModelMessages = defineMessages({
  apply: {
    id: 'components.app.analytics.createActionModel.apply',
    defaultMessage: 'Apply'
  },
  cancel: {
    id: 'components.app.analytics.createActionModel.cancel',
    defaultMessage: 'Cancel'
  },
  inputLabel: {
    id: 'components.app.analytics.createActionModel.inputLabel',
    defaultMessage: 'Enter a name for the action'
  },
  modelHeading: {
    id: 'components.app.analytics.createActionModel.modelHeading',
    defaultMessage: 'Create new action'
  },
  newAction: {
    id: 'components.app.analytics.createActionModel.newAction',
    defaultMessage: 'New action'
  }
});
export default messages;
